/**
 * CobrandTabContainer Component
 *
 * An organism component that provides a container for tabbed cobrand card content.
 * Integrates with AEM for content management and supports both author and publish modes.
 *
 * Key Features:
 * - AEM integration with EditableComponent and ResponsiveGrid
 * - Support for both author and publish modes
 * - CardType filtering for personal/business/none cards
 * - Comparison tray integration
 * - Dynamic content rendering based on tab selection
 *
 * @module organisms/CobrandTabContainer
 */

import React, { FC, ReactElement, useEffect, useState, useMemo, useCallback, memo } from 'react';
import clsx from 'clsx';
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { AEMReactCompMap, respGridUtil, updateAEMCQKeys } from '@marriott/mi-ui-library';
import { CobrandTabs } from '../../molecules/CobrandTabs';
import { CobrandComparisonTray } from '../CobrandComparisonTray/CobrandComparisonTray';
import { useStore } from '../../modules/store/cobrandStore';
import { StyledCobrandTabContainer } from './CobrandTabContainer.styles';
import { TabProps, TabContainerProps, TabDetails, CardType } from './CobrandTabContainer.types';

/**
 * AEM configuration for TabContainer component
 */
export const TabContainerConfig = {
  emptyLabel: 'CobrandTabContainer',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/cobrandtabcontainer`,
};

/**
 * TabContainerComp - Main component logic
 *
 * Handles rendering of tab container with AEM integration.
 * Supports author mode for editing and publish mode for end users.
 */
export const TabContainerComp: FC<TabProps> = memo<TabProps>((props: TabProps) => {
  // Destructure props with clear naming
  const {
    header,
    headerTag,
    subHeader,
    trackingProperties,
    styleclass,
    componentId,
    pagePath,
    itemPath,
    isAuthorMode,
    contentBlockAlignment,
    headerFontSize,
    subFontSize,
    subHeaderTag,
    descriptionText,
    descFontSize,
    tabDetails = [],
    layoutContainer,
    resolvedUrl,
    isCardComparisonFeatureEnabled,
    isComparisonTrayEnabled,
    targetExperience,
    allowedComponents,
    tabContainerVariation,
  } = props;

  // Use isAuthorMode with fallback
  const authorMode = isAuthorMode ?? false;

  // Update AEM CQ keys for proper content mapping
  const updatedProps = useMemo(() => updateAEMCQKeys(props), [props]);
  const cqItems = updatedProps['cqItems'];

  // Get cobrand store data
  const { cobrandData } = useStore(store => store);

  // Create AEM component mapper
  const mapper = useMemo(() => AEMReactCompMap(allowedComponents), [allowedComponents]);

  // State for edit mode detection
  const [isEditMode, setEditMode] = useState(false);

  /**
   * Effect to detect AEM edit mode
   * Observes HTML class changes to determine if in Edit or Preview mode
   */
  useEffect(() => {
    const pageWcmMode = document.getElementsByTagName('html')[0];
    if (!pageWcmMode) return;

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const currWcmMode = pageWcmMode.getAttribute('class');
          if (!currWcmMode) return;

          if (currWcmMode.includes('Edit')) {
            setEditMode(true);
          } else if (currWcmMode.includes('Preview')) {
            setEditMode(false);
          }
        }
      });
    });

    observer.observe(pageWcmMode, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);
  /**
   * Sort items based on AEM itemsOrder
   * @param card - Card object with :items and :itemsOrder properties
   * @returns Sorted items object
   */
  const getItemsSorted = useCallback((card: Record<string, unknown>) => {
    const items = card[':items'] as Record<string, unknown>;
    const itemsOrder = card[':itemsOrder'] as string[];

    if (!items || !itemsOrder) return {};

    const itemEntries = Object.entries(items);
    const sortedEntries = itemEntries.sort((a, b) => {
      const indexA = itemsOrder.indexOf(a[0]);
      const indexB = itemsOrder.indexOf(b[0]);
      return indexA - indexB;
    });

    return Object.fromEntries(sortedEntries);
  }, []);

  /**
   * Render component wrapper for AEM authoring mode
   * Provides a container that allows authors to add and edit components
   *
   * @param index - Index of the tab
   * @returns ResponsiveGrid component for authoring
   */
  const authorComponentWrapper = useCallback(
    (index: number) => {
      const tabDetail = tabDetails[index];
      if (!tabDetail || !tabDetail.nodeName) return null;

      const gridProps = {
        isInEditor: authorMode,
        pagePath,
        itemPath: `${itemPath}/${tabDetail.nodeName}`,
        columnCount: '12',
        gridClassNames: '',
        columnClassNames: {},
        allowedComponents: allowedComponents as never,
        customClassName: '',
        config: {
          isEmpty: () => true,
          resourceType: 'mi-aem-common-spa/components/container',
        },
      };

      return <ResponsiveGrid {...(gridProps as Parameters<typeof ResponsiveGrid>[0])} />;
    },
    [tabDetails, pagePath, itemPath, authorMode, allowedComponents]
  );

  /**
   * Render component wrapper for publish/end-user mode
   * Renders actual content based on AEM data with cardType filtering
   *
   * @param jsonData - AEM JSON data
   * @param idx - Index of the current tab
   * @returns Rendered components for the tab or null
   */
  const publishComponentWrapper = useCallback(
    (jsonData: Record<string, unknown>, idx: number): ReactElement | null => {
      const tabDetail = tabDetails[idx];
      if (!tabDetail || !tabDetail.nodeName) return null;

      // Get current tab's cardType for filtering
      const currentTabCardType: CardType = tabDetail.cardType || 'none';

      // Create a copy to avoid mutating original data
      const dataCopy = { ...jsonData };
      delete dataCopy['tabDetails'];

      // Check if tab node exists in data
      const nodeName = tabDetail.nodeName;
      if (!Object.prototype.hasOwnProperty.call(dataCopy, nodeName)) {
        return null;
      }

      const page = dataCopy[nodeName] as Record<string, unknown>;
      const pageItems = getItemsSorted(page);
      const renderedComponents: ReactElement[] = [];

      // Iterate through page items and render them
      for (const itemKey in pageItems) {
        if (!Object.prototype.hasOwnProperty.call(pageItems, itemKey)) continue;

        const item = pageItems[itemKey] as Record<string, unknown>;
        const itemType = (item[':type'] as string)?.split('/')?.pop();

        if (!itemType || !Object.prototype.hasOwnProperty.call(mapper, itemType)) continue;

        // Get item's cardType, default to 'none' for backward compatibility
        const itemCardType: CardType = (item['cardType'] as CardType) || 'none';

        // Filter based on cardType:
        // - If tab is 'none', show all components
        // - If tab is 'personal' or 'business', only show matching cardType or 'none'
        const shouldRenderItem =
          currentTabCardType === 'none' || itemCardType === 'none' || itemCardType === currentTabCardType;

        if (!shouldRenderItem) continue;

        // Build item props with all necessary data
        const itemProps = {
          ...item,
          componentPath: props?.componentPath,
          resolvedUrl,
          isCardComparisonFeatureEnabled,
          isComparisonTrayEnabled,
          // Hide CTA component if comparison tray is enabled
          styleClass: isComparisonTrayEnabled && itemKey.includes('cta_component') ? 'd-none' : '',
          targetExperience,
          // Ensure cardType is passed to child component
          cardType: itemCardType,
        };

        // Create grid data for ResponsiveGrid
        const reqJsonData = { ...page };
        reqJsonData[':items'] = { [itemKey]: itemProps };

        renderedComponents.push(
          <ResponsiveGrid model={respGridUtil(reqJsonData)} {...respGridUtil(reqJsonData)} key={itemKey} />
        );
      }

      return renderedComponents.length > 0 ? (
        <div className={clsx('col-12 override-container-style')}>{renderedComponents}</div>
      ) : null;
    },
    [
      tabDetails,
      getItemsSorted,
      mapper,
      props?.componentPath,
      resolvedUrl,
      isCardComparisonFeatureEnabled,
      isComparisonTrayEnabled,
      targetExperience,
    ]
  );

  return (
    <StyledCobrandTabContainer
      data-component-name="o-cobrand-CobrandTabContainer"
      data-testid="cobrand-CobrandTabContainer"
    >
      <div
        className={clsx(
          `${componentId}-container py-5 pb-md-0`,
          styleclass,
          't-background-color',
          tabContainerVariation === 'flag_section' ? 'flag-section-container' : ''
        )}
      >
        <CobrandTabs
          componentId={componentId}
          subHeader={subHeader}
          trackingProperties={trackingProperties}
          header={header}
          headerTag={headerTag}
          isAuthorMode={authorMode}
          contentBlockAlignment={contentBlockAlignment}
          headerFontSize={headerFontSize}
          subFontSize={subFontSize}
          subHeaderTag={subHeaderTag}
          descriptionText={descriptionText}
          descFontSize={descFontSize}
          tabDetails={tabDetails}
          layoutContainer={layoutContainer}
          isEditMode={isEditMode}
          componentPath={props?.componentPath}
        >
          {authorMode && Array.from({ length: tabDetails.length }, (_, i) => authorComponentWrapper(i))}
          {!authorMode && tabDetails.map((_tab: TabDetails, idx) => publishComponentWrapper(cqItems, idx))}
        </CobrandTabs>
      </div>
      {cobrandData?.selectedCards && <CobrandComparisonTray {...props} />}
    </StyledCobrandTabContainer>
  );
});

TabContainerComp.displayName = 'TabContainerComp';

/**
 * TabEditableComponent - AEM Editable Wrapper
 *
 * Wraps TabContainerComp with AEM EditableComponent for authoring support
 *
 * @param props - Component props including model
 * @returns Editable component for AEM
 */
export const TabEditableComponent = (props: TabContainerProps & { model?: TabProps }) => {
  const { model, ...restProps } = props;
  if (!model) return null;
  return (
    <EditableComponent config={TabContainerConfig} {...props}>
      <TabContainerComp {...model} {...restProps} componentName={props?.model?.cqType?.split('/')?.pop()} />
    </EditableComponent>
  );
};

/**
 * CobrandTabContainer - Main Export Component
 *
 * Top-level component that provides styling wrapper and integrates with AEM.
 * Memoized for performance optimization.
 *
 * @param props - Component props
 * @returns Styled and editable cobrand tab container
 */
export const CobrandTabContainer: FC<TabContainerProps> = memo<TabContainerProps>((props: TabContainerProps) => {
  return (
    <div className={clsx(props?.model?.styleclass)}>
      <TabEditableComponent {...props} />
    </div>
  );
});

CobrandTabContainer.displayName = 'CobrandTabContainer';
